import { isPlatformBrowser } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  PLATFORM_ID,
} from '@angular/core';
import type { EChartsCoreOption } from 'echarts/core';
import { NgxEchartsDirective } from 'ngx-echarts';
import { fmt1, fmtInt, fmtPeriod } from '../../../core/i18n/labels';
import { ThemeService } from '../../../core/services/theme.service';
import { baseText, NamedSeries, palette } from '../chart.types';

/** Multi/single line or bar trend chart. */
@Component({
  selector: 'app-trend-chart',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgxEchartsDirective],
  templateUrl: './trend-chart.component.html',
  styleUrl: './trend-chart.component.scss',
})
export class TrendChartComponent {
  private readonly theme = inject(ThemeService);
  readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));

  data = input.required<NamedSeries[]>();
  height = input<number>(280);
  kind = input<'line' | 'bar'>('line');
  unit = input<string>('');
  /** When true and 2 series are given, the 2nd gets its own right-hand axis. */
  dualAxis = input<boolean>(false);
  ariaLabel = input<string>('Графика на тенденция');

  options = computed<EChartsCoreOption>(() => {
    const dark = this.theme.theme() === 'dark';
    const colors = palette(dark);
    const t = baseText(dark);
    const all = this.data();
    const periodSet = new Set<string>();
    for (const s of all) for (const p of s.series) periodSet.add(p.period);
    const rawPeriods = [...periodSet].sort();
    const periods = rawPeriods.map(fmtPeriod);
    const multi = all.length > 1;
    const unit = this.unit();

    return {
      color: colors,
      grid: { left: 8, right: 12, top: multi ? 34 : 16, bottom: 8, containLabel: true },
      legend: multi
        ? { top: 0, textStyle: { color: t.muted, fontFamily: 'Sofia Sans' }, icon: 'roundRect' }
        : undefined,
      tooltip: {
        trigger: 'axis',
        backgroundColor: t.surface,
        borderColor: t.line,
        textStyle: { color: t.ink, fontFamily: 'Sofia Sans' },
        valueFormatter: (v: unknown) => `${fmt1(v as number)}${unit ? ' ' + unit : ''}`,
      },
      xAxis: {
        type: 'category',
        data: periods,
        boundaryGap: this.kind() === 'bar',
        axisLine: { lineStyle: { color: t.line } },
        axisLabel: { color: t.muted, fontFamily: 'Sofia Sans', hideOverlap: true },
        axisTick: { show: false },
      },
      yAxis: this.dualAxis()
        ? [
            {
              type: 'value',
              scale: true,
              splitLine: { lineStyle: { color: t.line } },
              axisLabel: { color: t.muted, fontFamily: 'Sofia Sans', formatter: (v: number) => fmtInt(v) },
            },
            {
              type: 'value',
              scale: true,
              splitLine: { show: false },
              axisLabel: { color: t.muted, fontFamily: 'Sofia Sans', formatter: (v: number) => fmtInt(v) },
            },
          ]
        : {
            type: 'value',
            scale: true,
            splitLine: { lineStyle: { color: t.line } },
            axisLabel: {
              color: t.muted,
              fontFamily: 'Sofia Sans',
              formatter: (v: number) => fmtInt(v),
            },
          },
      series: all.map((s, i) => {
        const byPeriod = new Map(s.series.map((p) => [p.period, p.value]));
        return {
          name: s.name,
          type: this.kind(),
          yAxisIndex: this.dualAxis() && i === 1 ? 1 : 0,
          smooth: 0.25,
          symbol: 'none',
          emphasis: { focus: multi ? 'series' : 'none' },
          lineStyle: { width: 2.4, type: s.projected ? 'dashed' : 'solid' },
          areaStyle:
            !multi && this.kind() === 'line' && !s.projected
              ? {
                  opacity: 0.14,
                  color: colors[i],
                }
              : undefined,
          data: rawPeriods.map((period) => byPeriod.get(period) ?? null),
        };
      }),
    };
  });
}
